/**
 * Callback Notification Provider
 * Plays sound and shows popup when new callback requests arrive
 */
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Phone, X, ArrowRight, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CallbackNotificationContext = createContext();

export const useCallbackNotification = () => useContext(CallbackNotificationContext);

// Urgent notification sound as base64
const URGENT_SOUND = 'data:audio/mp3;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA//tQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAABhgC7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7//////////////////////////////////////////////////////////////////8AAAAATGF2YzU4LjEzAAAAAAAAAAAAAAAAJAAAAAAAAAAAAYZEkBoLAAAAAAD/+9DEAAAGAAGn9AAAIuTJs/8MUAB4aW1lQUJTQUJTAAADMzMzMzP/+9DEGQPAAADSAAAAACAAGkAAAAATMzMzMzP/+9DEOAPAAADSAAAAACAAGkAAAAATMzMzM//7EMRFg8AAANIAAAAgAAA0gAAABMz/+xDEXYPAAADSAAAAIAAANIAAAAT/+xDEdYAAAADSAAAAAAAA0gAAAAD/+xDEigAAADSAAAAAAAA0gAAABP/7EMSYAAAA0gAAAAAAAADSAAAAE//sQxKAAAADSAAAAAAAAANIAAAAE//sQxLAAAADSAAAAAAAAANIAAAAA';

export const CallbackNotificationProvider = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [notifications, setNotifications] = useState([]);
  const [lastCount, setLastCount] = useState(0);
  const [soundEnabled, setSoundEnabledState] = useState(() => {
    // Load from localStorage, default to true
    const saved = localStorage.getItem('callbackSoundEnabled');
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const audioRef = useRef(null);
  const pollingRef = useRef(null);

  // Wrapper to persist sound preference
  const setSoundEnabled = (value) => {
    setSoundEnabledState(value);
    localStorage.setItem('callbackSoundEnabled', JSON.stringify(value));
  };

  // Check if user is logged in
  useEffect(() => {
    const token = localStorage.getItem('token');
    setIsLoggedIn(!!token);
  }, [location]);

  // Initialize audio
  useEffect(() => {
    audioRef.current = new Audio(URGENT_SOUND);
    audioRef.current.volume = 0.7;
  }, []);

  // Play notification sound
  const playSound = useCallback(() => {
    if (soundEnabled && audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(e => console.log('Audio play failed:', e));
    }
  }, [soundEnabled]);

  // Show notification popup
  const showNotification = useCallback((request) => {
    const id = Date.now();
    const notification = {
      id,
      name: request.name,
      phone: request.phone,
      preferredTime: request.preferred_time_label || request.preferred_time,
      language: request.language,
      timestamp: new Date()
    };

    setNotifications(prev => [...prev, notification]);
    playSound();

    // Request browser notification permission and show
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification('🚨 New Callback Request!', {
        body: `${request.name} - ${request.phone}\nPreferred: ${request.preferred_time_label || request.preferred_time}`,
        icon: '/favicon.ico',
        tag: 'callback-' + id,
        requireInteraction: true
      });
    }

    // Auto-dismiss after 15 seconds
    setTimeout(() => {
      dismissNotification(id);
    }, 15000);
  }, [playSound]);

  // Dismiss notification
  const dismissNotification = useCallback((id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  // Poll for new callback requests
  const checkForNewCallbacks = useCallback(async () => {
    if (!isLoggedIn) return;

    try {
      const response = await fetch(`${API_URL}/api/website/callback-requests?status=pending`);
      if (response.ok) {
        const data = await response.json();
        const currentCount = data.requests?.length || 0;
        
        // If there are new pending requests, show notifications for them
        if (currentCount > lastCount && lastCount > 0) {
          const newRequests = data.requests?.slice(0, currentCount - lastCount) || [];
          newRequests.forEach(request => {
            showNotification(request);
          });
        }
        
        setLastCount(currentCount);
      }
    } catch (error) {
      console.error('Error checking callbacks:', error);
    }
  }, [isLoggedIn, lastCount, showNotification]);

  // Start polling when logged in
  useEffect(() => {
    if (isLoggedIn) {
      // Initial check
      checkForNewCallbacks();
      
      // Poll every 15 seconds for new callbacks
      pollingRef.current = setInterval(checkForNewCallbacks, 15000);
      
      // Request notification permission
      if ('Notification' in window && Notification.permission === 'default') {
        Notification.requestPermission();
      }
    }

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [isLoggedIn, checkForNewCallbacks]);

  // Go to callback dashboard
  const handleViewAll = useCallback(() => {
    navigate('/callback-requests');
  }, [navigate]);

  // Call the customer directly
  const handleCall = useCallback((phone) => {
    window.location.href = `tel:${phone}`;
  }, []);

  return (
    <CallbackNotificationContext.Provider value={{ soundEnabled, setSoundEnabled }}>
      {children}
      
      {/* Notification Popups */}
      {notifications.length > 0 && (
        <div className="fixed bottom-4 right-4 z-50 space-y-3 max-w-sm">
          {notifications.map((notification) => (
            <div
              key={notification.id}
              className="bg-gradient-to-r from-red-600 to-red-700 rounded-2xl shadow-2xl border border-red-500 overflow-hidden animate-slide-in-right"
              style={{
                animation: 'slideInRight 0.3s ease-out'
              }}
            >
              {/* Urgent Banner */}
              <div className="bg-red-800 px-4 py-2 flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-white animate-pulse" />
                <span className="text-white text-xs font-bold uppercase tracking-wide">
                  Urgent Callback Request
                </span>
              </div>
              
              {/* Content */}
              <div className="p-4">
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-bold text-lg truncate">{notification.name}</p>
                    <a 
                      href={`tel:${notification.phone}`}
                      className="text-white/90 text-sm hover:underline"
                    >
                      {notification.phone}
                    </a>
                    <p className="text-white/70 text-xs mt-1">
                      Preferred: {notification.preferredTime}
                    </p>
                    <p className="text-white/50 text-xs">
                      {notification.language === 'tr' ? '🇹🇷 Turkish Speaker' : '🇬🇧 English Speaker'}
                    </p>
                  </div>
                  <button
                    onClick={() => dismissNotification(notification.id)}
                    className="p-1 hover:bg-white/10 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5 text-white/70" />
                  </button>
                </div>
                
                {/* Action Buttons */}
                <div className="flex gap-2 mt-4">
                  <button
                    onClick={() => handleCall(notification.phone)}
                    className="flex-1 bg-white text-red-600 py-2.5 px-4 rounded-xl font-bold text-sm hover:bg-gray-100 transition-colors flex items-center justify-center gap-2"
                  >
                    <Phone className="w-4 h-4" />
                    Call Now
                  </button>
                  <button
                    onClick={handleViewAll}
                    className="flex-1 bg-white/20 text-white py-2.5 px-4 rounded-xl font-semibold text-sm hover:bg-white/30 transition-colors flex items-center justify-center gap-2"
                  >
                    View All
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* Animation Keyframes */}
      <style>{`
        @keyframes slideInRight {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        .animate-slide-in-right {
          animation: slideInRight 0.3s ease-out;
        }
      `}</style>
    </CallbackNotificationContext.Provider>
  );
};

export default CallbackNotificationProvider;
